import React from "react";

import styles from "./BotForm.module.css";
import {Button, ButtonState} from "../../../../elements/button/Button";
import {RootState} from "../../../../../app/store";
import {ValidatedForm} from "../../../../elements/validator/validatedForm/ValidatedForm";
import {
    BotType,
    CcRecipientDTO,
    Country,
    CreateBotRequestOpenAiModelEnum,
    LocaleTag,
    MatchField,
    MatchType,
    RedirectProvider,
    SupportedTimezone
} from "../../../../../api/generated";
import {FormHeader} from "../../../../elements/formHeader/FormHeader";
import {AssistantSetUp} from "./components/AssistantSetUp";
import {CreateBotRequestUI} from "../../botCreateSlice";
import {DealerInformation} from "./components/DealerInformation";
import {UpdateBotRequestUI} from "../../botUpdateSlice";
import {SelectOption} from "../../../../elements/select/SelectDropdown";
import {UrlProcessor} from "./components/UrlProcessor";
import {BotTypeChangeWarning} from "./components/BotTypeChangeWarning";
import {FranchiseRequestUi} from "../../franchiseTypes";

export type BotRequest = CreateBotRequestUI | UpdateBotRequestUI
export type BotRequestPartial = Partial<BotRequest>

export type BotFormSelectionProvider = ({
    publicId: (state: RootState) => string | undefined;
    siteName: (state: RootState) => string | undefined;
    siteURL: (state: RootState) => string | undefined;
    groupHash: (state: RootState) => string | undefined;
    conversationLimit: (state: RootState) => number | undefined;
    workingRange: (state: RootState) => number | undefined;
    teamInfo: (state: RootState) => string | undefined;
    openAiApiKey: (state: RootState) => string | undefined;
    openAiModel: (state: RootState) => CreateBotRequestOpenAiModelEnum | undefined;
    lastRenewalDate: (state: RootState) => string | undefined;
    franchiseList: (state: RootState) => FranchiseRequestUi[] | undefined;

    redirectProvider: (state: RootState) => RedirectProvider | undefined;

    urlProcessorEnabled: (state: RootState) => boolean | undefined;
    matchField: (state: RootState) => MatchField | undefined;
    matchType: (state: RootState) => MatchType | undefined;
    segmentNumberFromTheEnd: (state: RootState) => number | undefined;
    timezone: (state: RootState) => SupportedTimezone | undefined;
    defaultLocale: (state: RootState) => LocaleTag | undefined;
    supportedPhoneNumberLocales: (state: RootState) => Country[] | undefined;

    botType: (state: RootState) => BotType | undefined;
    useMultiAgent: (state: RootState) => boolean | undefined;
    vehicleProviderWarningEmailEnabled: (state: RootState) => boolean | undefined;
    externalCommunicationsDisabled: (state: RootState) => boolean | undefined;
    assistantEmail: (state: RootState) => string | undefined;
    assistantEmailPassword: (state: RootState) => string | undefined;
    emailTranscriptRecipients: (state: RootState) => string | undefined;
    ccRecipients: (state: RootState) => CcRecipientDTO[] | undefined;
    filtrationEnabled: (state: RootState) => boolean | undefined;
    transcriptSummaryEnabled: (state: RootState) => boolean | undefined;
    sharingLinkEnabled: (state: RootState) => boolean | undefined;
});

interface Props {
    isUpdateForm?: boolean;
    formTitle: string
    onClose: () => any;
    onSubmit: () => any;
    franchiseOptions: SelectOption<FranchiseRequestUi>[]
    updateRequest: (update: BotRequestPartial) => void;
    selectionProvider: BotFormSelectionProvider;
}

export const BotForm = <T extends BotRequest>({
                                                  isUpdateForm = false,
                                                  formTitle,
                                                  onClose,
                                                  onSubmit,
                                                  updateRequest,
                                                  franchiseOptions,
                                                  selectionProvider
                                              }: Props) => {

    return <ValidatedForm<T> submitForm={onSubmit}>
        {(formCtx) => {
            const {formDisabled, onSubmitClick, updateValidationStatus, getMessage} = formCtx;
            return <div className={styles.wrapper}>
                <FormHeader
                    formTitle={formTitle}
                    onClose={onClose}
                />
                <div className={styles.fieldsWrapper}>
                    <DealerInformation<T>
                        getMessage={getMessage}
                        updateRequest={updateRequest}
                        franchiseOptions={franchiseOptions}
                        selectionProvider={selectionProvider}
                        updateValidationStatus={updateValidationStatus}
                    />
                    <UrlProcessor<T>
                        getMessage={getMessage}
                        updateRequest={updateRequest}
                        selectionProvider={selectionProvider}
                        updateValidationStatus={updateValidationStatus}
                    />
                    <AssistantSetUp<T>
                        getMessage={getMessage}
                        updateRequest={updateRequest}
                        selectionProvider={selectionProvider}
                        updateValidationStatus={updateValidationStatus}
                    />
                </div>
                {isUpdateForm && <BotTypeChangeWarning/>}
                    <div className={styles.buttonsWrapper}>
                <Button
                    title={"Close"}
                    state={ButtonState.SECONDARY}
                    handleClick={() => onClose()}
                />
                <Button
                    title={formTitle}
                    state={formDisabled ? ButtonState.DISABLED : ButtonState.PRIMARY}
                    handleClick={() => onSubmitClick()}
                />
            </div>
        </div>
        }}
    </ValidatedForm>
}
